import { useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'zustand/react';
import { useTranslations } from '@components/context';
import { createStepAccessStore } from '@stores/stepAccess/StepAccess.store';
import { selectPeople, selectSteps, selectTranslations } from '@stores/stepAccess/StepAccess.selectors';
import { participationEndedByRemoving } from './helper';
import StepAccessSection from './StepAccessSection';

// One store per position, and one render slot the flow editor mounts per step -- the same shape the
// per-step automations use, so both surfaces that host the editor get this with no second wiring.
export function useStepAccess(jobId, { savedStepIds = new Set() } = {}) {
    const store = useMemo(() => createStepAccessStore(jobId), [jobId]);
    const { t, updateTranslations } = useTranslations();
    const fetchAccess = useStore(store, (state) => state.fetchAccess);
    const translations = useStore(store, selectTranslations);
    const people = useStore(store, selectPeople);
    const steps = useStore(store, selectSteps);

    useEffect(() => {
        if (!jobId) {
            return undefined;
        }
        const controller = new AbortController();
        fetchAccess(controller.signal);
        return () => controller.abort();
    }, [jobId, fetchAccess]);

    useEffect(() => {
        updateTranslations(translations);
    }, [translations, updateTranslations]);

    const renderStepAccessForStep = useCallback(
        (stepId) => (
            <StepAccessSection
                key={`step-access-${stepId}`}
                store={store}
                stepId={stepId}
                savedStepIds={savedStepIds}
            />
        ),
        [store, savedStepIds],
    );

    // Composed here rather than served, because it is said BEFORE the request that would make it true:
    // the reader is about to delete a step, not to remove anyone from the hiring team.
    const warningForRemovingStep = useCallback(
        (stepId) => {
            const ending = participationEndedByRemoving({
                people,
                stepIds: steps.map((step) => step.step_id),
                stepId,
            });
            if (ending.length === 0) {
                return null;
            }
            return t(
                'Removing this step also removes {names} from the hiring team, because it is the only step they can see.',
                { names: ending.map((person) => person.text).join(', ') },
            );
        },
        [people, steps, t],
    );

    // A saved flow has different steps, so who is withheld what has changed.
    const refetchAccess = useCallback(() => fetchAccess(), [fetchAccess]);

    return { renderStepAccessForStep, warningForRemovingStep, refetchAccess };
}
